import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HeaderHeight, PageHeaderHeight } from '../Constants';
import UserMoreMenu from './_dashboard/smartlock/UserMoreMenu';
import AddNewUserModal from './_dashboard/smartlock/AddNewUserModal';
import ConfirmAlertDialog from './ConfirmDialog';
import { useAuth } from './customContextProviders/AuthContext';
import NoDataAvailable from './_dashboard/smartlock/NoDataAvailable';
import { DeleteUserById, GetAllUsers } from '../network/User';
import { GetAllOrganizationList } from '../network/Organization';

const useStyles = makeStyles((theme) => ({
  menuItem: {
    fontSize: '14px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px'
    },
    minHeight: '20px',
    borderRadius: '8px',
    marginInline: theme.spacing(1)
  },
  tableCellStyle: {
    fontSize: {
      xs: '12px',
      md: '14px'
    },
    fontWeight: 700,
    padding: {
      xs: '8px',
      md: '16px'
    },
    textWrap: 'nowrap',
    backgroundColor: '#FFFFFF'
  },
  tablePaper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF'
  },
  tableContainer: {
    flexGrow: 1,
    height: `calc(100vh - ${HeaderHeight + PageHeaderHeight + 56 + 40 + 50 + 18}px)`,
    overflow: 'auto'
  }
}));

const LoaderContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50px' // Adjust height as needed
});
const PageHeader = styled('Box')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1,
  flexDirection: 'row !important',
  height: PageHeaderHeight
}));
export default function UserManagement() {
  const classes = useStyles();
  const { sessionUserData } = useAuth();

  const userRole = sessionUserData?.user?.userRole?.roleName;

  const [userList, setUserList] = useState([]);
  const [organizationList, setOrganizationList] = useState([]);
  const [filterOrganization, setFilterOrganization] = useState('');
  const [isAssignAddModal, setIsAssignAddModal] = useState(false);
  const [openConfirmAlertDialog, setOpenConfirmAlertDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState({});
  const [confirmAlertDialogMetas, setConfirmAlertDialogMetas] = useState({
    message: '',
    title: '',
    positiveBtnLable: '',
    negativeBtnLabel: ''
  });
  const [alertMetas, setAlertMetas] = useState({
    snackBarOpened: false,
    vertical: 'top',
    horizontal: 'right',
    msg: '',
    variant: 'filled',
    severity: ''
  });
  const vertical = 'bottom';
  const horizontal = 'left';

  const handleSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertMetas({ ...alertMetas, snackBarOpened: false });
  };

  const handleFilterChange = (e) => {
    setFilterOrganization(e.target.value);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleUserMoreMenuClick = (action, data) => {
    switch (action) {
      case 'edit_user': {
        setSelectedUserData(data);
        setIsAssignAddModal(true);
        break;
      }
      case 'delete_user': {
        setSelectedUserData(data);
        setConfirmAlertDialogMetas({
          ...confirmAlertDialogMetas,
          message: 'Are you sure want to delete this user ?',
          title: 'Delete',
          positiveBtnLable: 'Confirm',
          negativeBtnLabel: 'Cancel'
        });
        setOpenConfirmAlertDialog(true);
        break;
      }
      default:
        return '';
    }
  };

  const deleteUserById = async (id) => {
    try {
      const res = await DeleteUserById(id);
      if (res) {
        setAlertMetas({
          ...alertMetas,
          severity: 'error',
          snackBarOpened: true,
          msg: 'User Deleted !'
        });
        setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
        onAddUserModalAction();
      }
    } catch (error) {
      setAlertMetas({
        ...alertMetas,
        severity: 'error',
        snackBarOpened: true,
        msg: error?.response?.data?.message
      });
      setTimeout(() => setAlertMetas({ ...alertMetas, snackBarOpened: false, msg: '' }), 4000);
    }
  };

  const getAllUser = async (page, filterOrganization) => {
    setLoader(true);
    try {
      const res = await GetAllUsers(page, filterOrganization);
      if (res) {
        setUserList(res?.data);
      }
    } catch (error) {
      console.log(error, 'err');
    } finally {
      setLoader(false);
    }
  };

  const getAllOrganization = async () => {
    try {
      const res = await GetAllOrganizationList();
      if (res) {
        setOrganizationList(res?.data?.data);
      }
    } catch (error) {
      console.log(error?.response?.data?.message, 'error');
    }
  };

  const onAddUserModalAction = (d) => {
    getAllUser(page, filterOrganization);
  };

  useEffect(() => {
    getAllUser(page, filterOrganization);
  }, [page, filterOrganization]);

  useEffect(() => {
    if (userRole?.toUpperCase() === 'SUPERADMIN') {
      getAllOrganization();
    }
  }, []);

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={alertMetas?.snackBarOpened}
        onClose={handleSnackBarClose}
        autoHideDuration={6000}
      >
        <Alert variant={alertMetas?.variant} severity={alertMetas?.severity}>
          {alertMetas?.msg}
        </Alert>
      </Snackbar>
      <Card sx={{ width: '100%', padding: '10px' }}>
        <Box>
          <PageHeader>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              <Button
                variant="outlined"
                size="medium"
                endIcon={<PersonAddAltOutlinedIcon />}
                onClick={() => setIsAssignAddModal(true)}
                sx={{ backgroundColor: '#FFFFFF' }}
              >
                Add
              </Button>
            </Stack>
            {userRole?.toUpperCase() === 'SUPERADMIN' && (
              <FormControl data-testid="user-filter-by" size="small" sx={{ minWidth: { xs: 130, sm: 150 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '14px' }, fontWeight: 600 }}>
                    Filter By
                  </Typography>
                  <Select
                    sx={{
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: '500',
                      minWidth: '130px',
                      borderRadius: '12px'
                    }}
                    labelId="role-filter-select-label"
                    id="role-filter-select"
                    value={filterOrganization}
                    onChange={handleFilterChange}
                    IconComponent={ExpandMoreIcon}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 180,
                          overflowY: 'auto'
                        }
                      }
                    }}
                  >
                    {organizationList?.map((item) => (
                      <MenuItem
                        value={item?.id}
                        key={item?.id}
                        className={classes.menuItem}
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {item?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </FormControl>
            )}
          </PageHeader>
          <Box sx={{ width: '100%', typography: 'body1', borderTop: '1px solid #EBEBEB' }}>
            <Paper className={classes.tablePaper}>
              <TableContainer data-testid="user-data-table" className={classes.tableContainer}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        Name
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        E-mail
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        Phone Number
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100}>
                        Role
                      </TableCell>
                      <TableCell className={classes.tableCellStyle} width={100} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList?.data?.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <NoDataAvailable />
                        </TableCell>
                      </TableRow>
                    )}
                    {loader ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <LoaderContainer>
                            <CircularProgress size={30} />
                          </LoaderContainer>
                        </TableCell>
                      </TableRow>
                    ) : (
                      userList?.data?.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell
                            align="left"
                            sx={{
                              textTransform: 'capitalize',
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                          >
                            {item.name}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="left"
                          >
                            {item?.email}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              textTransform: 'capitalize',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="left"
                          >
                            {item?.phone}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{
                              textTransform: 'capitalize',
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                          >
                            {item?.userRole?.roleName}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: '5vw',
                              textWrap: 'nowrap',
                              textTransform: 'capitalize',
                              padding: {
                                xs: '8px',
                                md: '16px'
                              },
                              fontSize: {
                                xs: '12px',
                                md: '14px' // Larger screens
                              }
                            }}
                            align="right"
                          >
                            <UserMoreMenu handleClicks={(e) => handleUserMoreMenuClick(e, item)} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            {userList?.data?.length !== 0 && (
              <Stack
                sx={{
                  marginTop: '10px',
                  marginBottom: '10px'
                }}
              >
                <Pagination
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                  count={userList?.totalPages}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  page={page}
                  total={userList?.total}
                />
              </Stack>
            )}
          </Box>
        </Box>
      </Card>
      {isAssignAddModal && (
        <AddNewUserModal
          openAssignedModal={isAssignAddModal}
          handleClose={() => setIsAssignAddModal(false)}
          userData={selectedUserData}
          organizationList={organizationList}
          setAlertMetas={setAlertMetas}
          alertMetas={alertMetas}
          onAction={(d) => onAddUserModalAction(d)}
        />
      )}
      <ConfirmAlertDialog
        openConfirmAlertDialog={openConfirmAlertDialog}
        message={confirmAlertDialogMetas?.message}
        title={confirmAlertDialogMetas?.title}
        positiveBtnLable={confirmAlertDialogMetas?.positiveBtnLable}
        negativeBtnLable={confirmAlertDialogMetas?.negativeBtnLabel}
        handleClose={() => setOpenConfirmAlertDialog(false)}
        handlePositiveClose={() => {
          deleteUserById(selectedUserData?.id);
          setOpenConfirmAlertDialog(false);
        }}
        handleNegativeClose={() => setOpenConfirmAlertDialog(false)}
      />
    </Box>
  );
}
